/**
 * Column mapping + row validation for the bulk identity importer.
 *
 * Everything here is pure so it can be unit tested without a DOM — the page
 * component owns the network calls.
 */

import { DateTime } from 'luxon'

export type ImportFieldKey =
    | 'fullName'
    | 'firstName'
    | 'middleName'
    | 'lastName'
    | 'email'
    | 'companyName'
    | 'employeeNumber'
    | 'payRate'
    | 'mobilePhoneNumber'
    | 'homePhoneNumber'
    | 'streetAddress'
    | 'city'
    | 'province'
    | 'country'
    | 'postalCode'
    | 'clientBadgeRoleName'
    | 'printableNotes'
    | 'dateOfBirth'
    | 'pictureUrl'

/** Sentinel used by the mapping dropdown for "don't import this column". */
export const IGNORE_COLUMN = '__ignore__'

export interface ImportField {
    key: ImportFieldKey
    label: string
    /** Extra header spellings that auto-map onto this field. */
    aliases: string[]
    /**
     * `create` fields ride along on POST /identity; `patch` fields are applied
     * afterwards one PATCH at a time (the API has no bulk create). `split` is
     * fanned out into other fields, and `picture` is downloaded then PATCHed —
     * both are handled by the page, not by `patchFieldsFor`.
     */
    apply: 'create' | 'patch' | 'split' | 'picture'
    /** PATCH field name, when it differs from `key`. */
    patchField?: string
    group: 'name' | 'contact' | 'work' | 'address' | 'other'
    /**
     * Cleans a trimmed cell into the value we send. Throws when the cell can't
     * be understood, which surfaces as a per-row validation error.
     */
    normalize?: (value: string) => string
    /** Human-readable hint shown under the field in the mapping table. */
    hint?: string
    /** Sample value used by the downloadable template's example row. */
    example?: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Date spellings we accept for date of birth, most specific first. */
const DATE_FORMATS = [
    'yyyy-MM-dd',
    'yyyy/MM/dd',
    'MM/dd/yyyy',
    'M/d/yyyy',
    'MM-dd-yyyy',
    'M-d-yyyy',
    'dd/MM/yyyy',
    'd/M/yyyy',
    'dd-MMM-yyyy',
    'd MMMM yyyy',
    'MMMM d yyyy',
    'MMM d yyyy',
]

/**
 * Parses a date-of-birth cell into the epoch-millis string the PATCH endpoint
 * expects. Anchored at UTC noon so a timezone shift can't roll the day over.
 */
export const parseDateOfBirth = (value: string): string => {
    const cleaned = value.replace(/,/g, ' ').replace(/\s+/g, ' ').trim()

    let parsed = DateTime.fromISO(cleaned, { zone: 'utc' })
    if (!parsed.isValid) {
        for (const format of DATE_FORMATS) {
            const attempt = DateTime.fromFormat(cleaned, format, { zone: 'utc' })
            if (attempt.isValid) {
                parsed = attempt
                break
            }
        }
    }
    if (!parsed.isValid) {
        throw new Error(`"${value}" is not a date we recognize (try YYYY-MM-DD)`)
    }
    if (parsed.year < 1900 || parsed > DateTime.utc()) {
        throw new Error(`"${value}" is not a plausible date of birth`)
    }
    return String(parsed.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toMillis())
}

/** Strips currency decoration so "$18.50 /hr" imports as "18.50". */
const normalizePayRate = (value: string): string => {
    const cleaned = value.replace(/[$,]/g, '').replace(/\s*\/?\s*(hr|hour|hourly)\.?$/i, '').trim()
    return cleaned || value
}

const normalizeEmail = (value: string): string => {
    if (!EMAIL_PATTERN.test(value)) {
        throw new Error(`"${value}" is not a valid email address`)
    }
    return value
}

/** Collapses runs of whitespace — phone numbers and addresses paste in messy. */
const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ')

/**
 * Hosts whose "share" links serve an HTML viewer page rather than the image
 * itself. Rewriting them to the direct-download form means a pasted Drive or
 * Dropbox link works without the user having to hunt for the raw URL.
 */
const DIRECT_IMAGE_URL_REWRITES: Array<(url: URL) => string | undefined> = [
    // https://drive.google.com/file/d/<id>/view?usp=sharing
    (url) => {
        if (!/(^|\.)drive\.google\.com$/.test(url.hostname)) return undefined
        const id = url.pathname.match(/\/file\/d\/([^/]+)/)?.[1] || url.searchParams.get('id')
        return id ? `https://drive.google.com/uc?export=download&id=${id}` : undefined
    },
    // https://www.dropbox.com/s/<hash>/photo.jpg?dl=0
    (url) => {
        if (!/(^|\.)dropbox\.com$/.test(url.hostname)) return undefined
        url.searchParams.set('raw', '1')
        url.searchParams.delete('dl')
        return url.toString()
    },
]

/**
 * Validates a photo link and rewrites known share-link forms to the direct
 * image URL. Only http(s) is accepted — the browser does the downloading, so a
 * `file://` or `data:` cell would be meaningless here.
 */
export const normalizePictureUrl = (value: string): string => {
    const cleaned = value.trim()
    let url: URL
    try {
        url = new URL(cleaned)
    } catch {
        throw new Error(`"${value}" is not a URL (it needs to start with https://)`)
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new Error(`"${value}" is not an http(s) link`)
    }
    for (const rewrite of DIRECT_IMAGE_URL_REWRITES) {
        const rewritten = rewrite(new URL(cleaned))
        if (rewritten) return rewritten
    }
    return cleaned
}

export const IMPORT_FIELDS: ImportField[] = [
    {
        key: 'fullName',
        label: 'Full Name (split)',
        aliases: ['name', 'full name', 'fullname', 'employee name', 'staff name', 'display name'],
        apply: 'split',
        group: 'name',
        normalize: collapseWhitespace,
        hint: 'Split into first / last name',
        example: 'Jane Doe',
    },
    {
        key: 'firstName',
        label: 'First Name',
        aliases: ['first', 'firstname', 'first name', 'given name', 'givenname', 'fname', 'forename'],
        apply: 'create',
        group: 'name',
        normalize: collapseWhitespace,
        example: 'Jane',
    },
    {
        key: 'middleName',
        label: 'Middle Name',
        aliases: ['middle', 'middlename', 'middle name', 'middle initial', 'mi'],
        apply: 'patch',
        group: 'name',
        normalize: collapseWhitespace,
        example: 'Quinn',
    },
    {
        key: 'lastName',
        label: 'Last Name',
        aliases: ['last', 'lastname', 'last name', 'surname', 'family name', 'lname'],
        apply: 'create',
        group: 'name',
        normalize: collapseWhitespace,
        example: 'Doe',
    },
    {
        key: 'email',
        label: 'Email',
        aliases: ['email', 'e-mail', 'email address', 'emailaddress', 'mail', 'work email'],
        apply: 'create',
        group: 'contact',
        normalize: normalizeEmail,
        example: 'jane.doe@example.com',
    },
    {
        key: 'mobilePhoneNumber',
        label: 'Mobile Phone',
        aliases: ['mobile', 'cell', 'cell phone', 'cellphone', 'mobile phone', 'phone', 'phone number', 'mobile number'],
        apply: 'patch',
        group: 'contact',
        normalize: collapseWhitespace,
        example: '555-123-4567',
    },
    {
        key: 'homePhoneNumber',
        label: 'Home Phone',
        aliases: ['home', 'home phone', 'home phone number', 'landline', 'house phone'],
        apply: 'patch',
        group: 'contact',
        normalize: collapseWhitespace,
        example: '555-987-6543',
    },
    {
        key: 'companyName',
        label: 'Company',
        aliases: ['company', 'company name', 'employer', 'organization', 'organisation', 'org', 'vendor', 'agency'],
        apply: 'patch',
        group: 'work',
        normalize: collapseWhitespace,
        example: 'ACME Staffing',
    },
    {
        key: 'employeeNumber',
        label: 'Employee Number',
        aliases: ['employee number', 'employee #', 'employee id', 'employeeid', 'emp id', 'empno', 'staff id', 'staff number', 'payroll id', 'payroll number'],
        apply: 'patch',
        group: 'work',
        example: 'EMP-001',
    },
    {
        key: 'payRate',
        label: 'Pay Rate',
        aliases: ['pay rate', 'payrate', 'rate', 'wage', 'hourly rate', 'hourly', 'pay'],
        apply: 'patch',
        group: 'work',
        normalize: normalizePayRate,
        example: '18.50',
    },
    {
        key: 'clientBadgeRoleName',
        label: 'Badge Role',
        aliases: ['role', 'role name', 'badge role', 'badge role name', 'position', 'title', 'job title', 'job'],
        apply: 'patch',
        group: 'work',
        normalize: collapseWhitespace,
        hint: 'Role printed on the client badge',
        example: 'Supervisor',
    },
    {
        key: 'streetAddress',
        label: 'Street Address',
        aliases: ['address', 'street', 'street address', 'address 1', 'address1', 'address line 1', 'addressline1'],
        apply: 'patch',
        group: 'address',
        normalize: collapseWhitespace,
        example: '123 Main St',
    },
    {
        key: 'city',
        label: 'City',
        aliases: ['city', 'town', 'municipality'],
        apply: 'patch',
        group: 'address',
        normalize: collapseWhitespace,
        example: 'Toronto',
    },
    {
        key: 'province',
        label: 'Province / State',
        aliases: ['province', 'state', 'prov', 'state province', 'province state', 'region'],
        apply: 'patch',
        group: 'address',
        normalize: collapseWhitespace,
        example: 'ON',
    },
    {
        key: 'country',
        label: 'Country',
        aliases: ['country', 'nation'],
        apply: 'patch',
        group: 'address',
        normalize: collapseWhitespace,
        example: 'Canada',
    },
    {
        key: 'postalCode',
        label: 'Postal / Zip Code',
        aliases: ['postal code', 'postalcode', 'postal', 'post code', 'zip', 'zip code', 'zipcode'],
        apply: 'patch',
        group: 'address',
        normalize: (v) => v.toUpperCase().replace(/\s+/g, ' '),
        example: 'M5V 2T6',
    },
    {
        key: 'printableNotes',
        label: 'Printable Notes',
        aliases: ['notes', 'note', 'printable notes', 'comments', 'comment', 'remarks'],
        apply: 'patch',
        group: 'other',
        normalize: collapseWhitespace,
        example: 'Needs a parking pass',
    },
    {
        key: 'dateOfBirth',
        label: 'Date of Birth',
        aliases: ['dob', 'date of birth', 'dateofbirth', 'birthdate', 'birth date', 'birthday', 'born'],
        apply: 'patch',
        group: 'other',
        normalize: parseDateOfBirth,
        hint: 'Admin only — accepts YYYY-MM-DD, MM/DD/YYYY, …',
        example: '1990-04-17',
    },
    {
        key: 'pictureUrl',
        label: 'Photo URL',
        aliases: [
            'photo',
            'photo url',
            'photo link',
            'picture',
            'picture url',
            'picture link',
            'image',
            'image url',
            'image link',
            'headshot',
            'headshot url',
            'avatar',
            'avatar url',
            'profile picture',
            'profile photo',
        ],
        apply: 'picture',
        group: 'other',
        normalize: normalizePictureUrl,
        hint: 'Downloaded and saved as the profile photo',
        example: 'https://example.com/photos/jane-doe.jpg',
    },
]

export const FIELD_BY_KEY: Record<string, ImportField> = IMPORT_FIELDS.reduce(
    (acc, field) => {
        acc[field.key] = field
        return acc
    },
    {} as Record<string, ImportField>
)

/** Header text reduced to letters + digits, so `First Name` == `first_name`. */
export const normalizeHeader = (header: string): string =>
    (header ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

const ALIAS_LOOKUP: Record<string, ImportFieldKey> = (() => {
    const lookup: Record<string, ImportFieldKey> = {}
    for (const field of IMPORT_FIELDS) {
        for (const alias of [field.key, field.label, ...field.aliases]) {
            const normalized = normalizeHeader(alias)
            // First field to claim a spelling wins; IMPORT_FIELDS order is the tiebreak.
            if (normalized && !(normalized in lookup)) lookup[normalized] = field.key
        }
    }
    return lookup
})()

/** A column index -> field key (or `IGNORE_COLUMN`) map. */
export type ColumnMapping = Record<number, ImportFieldKey | typeof IGNORE_COLUMN>

/**
 * Best-effort guess at which identity field each CSV column holds. A field is
 * only claimed once — a second `Phone` column is left unmapped rather than
 * silently overwriting the first.
 */
export const autoMapColumns = (headers: string[]): ColumnMapping => {
    const mapping: ColumnMapping = {}
    const taken = new Set<ImportFieldKey>()

    headers.forEach((header, index) => {
        const key = ALIAS_LOOKUP[normalizeHeader(header)]
        if (key && !taken.has(key)) {
            mapping[index] = key
            taken.add(key)
        } else {
            mapping[index] = IGNORE_COLUMN
        }
    })

    // `fullName` is redundant once both halves are mapped explicitly.
    if (taken.has('fullName') && taken.has('firstName') && taken.has('lastName')) {
        for (const index of Object.keys(mapping).map(Number)) {
            if (mapping[index] === 'fullName') mapping[index] = IGNORE_COLUMN
        }
    }

    return mapping
}

/**
 * Splits a full name into first and last only — no middle name. The first token is the
 * first name and everything after it is the last name, so "Dick Van Dyke" becomes
 * first "Dick", last "Van Dyke". "Doe, Jane" (surname-first) is also understood.
 */
export const splitFullName = (value: string): { firstName: string; lastName: string } => {
    // "Doe, Jane" — comma form puts the surname first.
    if (value.includes(',')) {
        const [last, ...rest] = value.split(',')
        return {
            firstName: rest.join(',').trim().replace(/\s+/g, ' '),
            lastName: last.trim(),
        }
    }

    const parts = value.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return { firstName: '', lastName: '' }
    if (parts.length === 1) return { firstName: parts[0], lastName: '' }

    return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' '),
    }
}

export interface ImportRow {
    /** 1-based position in the CSV data rows, for error reporting. */
    rowNumber: number
    /** Raw (untrimmed) cells, kept so the error report can echo the original. */
    raw: string[]
    /** Field values to send, already trimmed and normalized. */
    values: Partial<Record<ImportFieldKey, string>>
    errors: string[]
    warnings: string[]
}

export interface BuildRowsOptions {
    /** Badge role applied when the row has no mapped/filled role column. */
    defaultBadgeRoleName?: string
    /** Skip rows whose email is already taken by an existing identity. */
    existingEmails?: Set<string>
}

/**
 * Turns raw CSV cells into validated, trimmed import rows. Rows carrying errors
 * are still returned — the UI lists them so the user can fix the source file.
 */
export const buildImportRows = (
    rows: string[][],
    mapping: ColumnMapping,
    options: BuildRowsOptions = {}
): ImportRow[] => {
    const defaultRole = (options.defaultBadgeRoleName ?? '').trim()
    const existingEmails = options.existingEmails
    const seenEmails = new Map<string, number>()

    return rows.map((raw, index) => {
        const row: ImportRow = {
            rowNumber: index + 1,
            raw,
            values: {},
            errors: [],
            warnings: [],
        }

        let fullName = ''

        Object.keys(mapping).forEach((columnKey) => {
            const column = Number(columnKey)
            const target = mapping[column]
            if (!target || target === IGNORE_COLUMN) return

            const cell = (raw[column] ?? '').trim()
            if (!cell) return

            const field = FIELD_BY_KEY[target]
            let value = cell
            if (field?.normalize) {
                try {
                    value = field.normalize(cell)
                } catch (error: any) {
                    row.errors.push(`${field.label}: ${error?.message || 'invalid value'}`)
                    return
                }
            }

            if (target === 'fullName') fullName = value
            else row.values[target] = value
        })

        if (fullName) {
            const split = splitFullName(fullName)
            if (!row.values.firstName && split.firstName) row.values.firstName = split.firstName
            if (!row.values.lastName && split.lastName) row.values.lastName = split.lastName
        }

        if (defaultRole && !row.values.clientBadgeRoleName) {
            row.values.clientBadgeRoleName = defaultRole
        }

        if (!row.values.firstName) row.errors.push('First name is missing')
        if (!row.values.lastName) row.errors.push('Last name is missing')

        const email = row.values.email
        if (email) {
            const lowered = email.toLowerCase()
            const firstSeen = seenEmails.get(lowered)
            if (firstSeen) {
                row.errors.push(`Email duplicates row ${firstSeen} of this file`)
            } else {
                seenEmails.set(lowered, row.rowNumber)
            }
            if (existingEmails?.has(lowered)) {
                row.errors.push('Email already belongs to an existing identity')
            }
        }

        return row
    })
}

/** Fields that go out as PATCH calls after the identity exists, in a stable order. */
export const patchFieldsFor = (row: ImportRow): Array<{ field: string; value: string }> =>
    IMPORT_FIELDS.filter((f) => f.apply === 'patch' && row.values[f.key])
        .map((f) => ({ field: f.patchField || f.key, value: row.values[f.key] }))

/** Summary counts for the review step. */
export const summarizeRows = (rows: ImportRow[]) => ({
    total: rows.length,
    valid: rows.filter((r) => r.errors.length === 0).length,
    invalid: rows.filter((r) => r.errors.length > 0).length,
})

/**
 * Columns in the downloadable template — every importable field except the
 * `Full Name (split)` convenience column, which would fight with the explicit
 * First / Last Name columns if both were filled in.
 */
export const TEMPLATE_FIELDS: ImportField[] = IMPORT_FIELDS.filter((f) => f.apply !== 'split')

export interface TemplateOptions {
    /** Adds a single filled-in row showing the format each column expects. */
    includeExampleRow?: boolean
}

/**
 * Header row (plus an optional example row) for the starter CSV. Only First and
 * Last Name are required: an empty cell is never sent, so unused columns can be
 * left blank or deleted outright.
 */
export const buildTemplateRows = (options: TemplateOptions = {}): string[][] => {
    const headers = TEMPLATE_FIELDS.map((field) => field.label)
    if (!options.includeExampleRow) return [headers]
    return [headers, TEMPLATE_FIELDS.map((field) => field.example ?? '')]
}
